import React from "react";
import { useMonyawn } from "../../context/MonyawnContext";
import { useChatThread } from "../../hooks/useChatThread";
import { stageMeta } from "../../workflow/constants";
import { ChatThread } from "./ChatThread";
import { ChatInput } from "./ChatInput";
import { AddProofModal } from "./modals/AddProofModal";
import { EditProfileModal } from "./modals/EditProfileModal";
import { ComposeDMModal } from "./modals/ComposeDMModal";

export const ChatShell: React.FC<{
  isIntakeMode?: boolean;
  onIntakeComplete?: (answers: Record<string, any>) => void;
}> = ({ isIntakeMode, onIntakeComplete }) => {
  const {
    selectedOpportunity,
    selectedProfile,
    opportunityProof,
    opportunityDMs,
    opportunityTasks,
    completionScore,
  } = useMonyawn();

  const {
    messages,
    openModal,
    setOpenModal,
    sendUserMessage,
    handleControlChange,
    handleControlSubmit,
    threadEndRef,
    isProcessing,
  } = useChatThread(selectedOpportunity, opportunityProof, selectedProfile, opportunityDMs, {
    isIntakeMode,
    onIntakeComplete,
  });

  const stage = selectedOpportunity?.current_stage;
  const meta = stage ? stageMeta[stage] : null;
  const blockingTasks = opportunityTasks.filter(
    (t) => t.blocking && t.status !== "completed",
  ).length;

  return (
    <div className="flex flex-col h-[calc(100vh-12rem)] min-h-[500px] rounded-3xl border border-black/8 bg-white/80 shadow-brand-shadow overflow-hidden">
      {/* Header bar */}
      <div className="flex items-center justify-between gap-4 px-5 py-3.5 border-b border-black/6 bg-white/90 flex-none">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className={`w-2.5 h-2.5 rounded-full flex-none ${isIntakeMode ? "bg-blue-500 animate-pulse" : "bg-brand-accent"}`}
          />
          <div className="min-w-0">
            <p className="text-sm font-bold text-brand-ink truncate">
              {isIntakeMode
                ? "New Play Intake"
                : selectedOpportunity
                  ? `${selectedOpportunity.company_name} — ${selectedOpportunity.role_title}`
                  : "No active play"}
            </p>
            {meta && !isIntakeMode && (
              <p className="text-xs text-brand-muted">
                {meta.label} · {meta.reviewerRole}
              </p>
            )}
            {isIntakeMode && (
              <p className="text-xs text-brand-muted">Conversation-led data capture</p>
            )}
          </div>
        </div>

        {!isIntakeMode && (
          <div className="flex items-center gap-3 flex-none">
            {blockingTasks > 0 && (
              <span className="text-[10px] font-bold uppercase tracking-widest bg-red-50 text-red-500 border border-red-100 px-2.5 py-1 rounded-full">
                {blockingTasks} blocked
              </span>
            )}
            <span className="text-[10px] font-bold uppercase tracking-widest bg-brand-highlight/30 text-brand-accent border border-brand-highlight/40 px-2.5 py-1 rounded-full">
              {completionScore}% ready
            </span>
          </div>
        )}
      </div>

      {/* Thread — fills remaining space */}
      <ChatThread
        messages={messages}
        threadEndRef={threadEndRef}
        onOpenModal={setOpenModal}
        onControlChange={handleControlChange}
        onControlSubmit={handleControlSubmit}
      />

      {/* Input bar — pinned to bottom */}
      <div className="flex-none px-4 pb-4 pt-2 bg-white/90 border-t border-black/5">
        <ChatInput
          onSend={sendUserMessage}
          onOpenModal={setOpenModal}
          disabled={(!selectedOpportunity && !isIntakeMode) || isProcessing}
          placeholder={
            isIntakeMode
              ? "Type your answer or paste a clue…"
              : selectedOpportunity
                ? `Message ${meta?.reviewerRole ?? "your play"}…`
                : "Select a play to start"
          }
        />
        <p className="text-[10px] text-brand-muted text-center mt-2">
          {isIntakeMode
            ? "Your responses are automatically captured into the play."
            : "Enter to send · Shift+Enter for new line · + to add proof · ✉ to draft a message"}
        </p>
      </div>

      {/* Modals */}
      {openModal === "add-proof" && <AddProofModal onClose={() => setOpenModal(null)} />}
      {openModal === "edit-profile" && <EditProfileModal onClose={() => setOpenModal(null)} />}
      {openModal === "compose-dm" && <ComposeDMModal onClose={() => setOpenModal(null)} />}
    </div>
  );
};
